import { Deserializer } from "jsonapi-serializer";
import { DeviceTimePattern } from "../models/DeviceTimePattern";
import { DeviceTimePatternWrapper } from "../models/wrappers/DeviceTimePatternWrapper";
import { convertToTime, convertToDuration, getDays, getFormattedDate, buildDayList, convertToEndTime } from "./utilities/dateFormatting";
import { createPagination } from "./utilities/paginationBuilder";
import { customFilterBuilder } from "./utilities/customFilterBuilder";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import { ListQuery } from "../models/ListQuery";
import { APIConnector } from "./api/APIConnector";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { convertDateToNumber, convertNumberToDate } from "../utilities/DateUtils";
import { Days } from "../models/Day";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { Filter } from "../models/Filter";
import { genericError } from "../models/GenericError";
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "../constants/response-status-codes";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { isSuccessStatus } from "../utilities/requestResponseValidator";
import { Translator } from "../contexts/translator";

const apiConnector = new APIConnector();

export const getDeviceTimePatternNames = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        let url = `timePattern?${partialURL}&include=site`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, deviceTimePatterns: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const deviceTimePatternNames: Name[] = deviceTimePatterns.map((deviceTimePattern): Name => {
                    return {
                        value: deviceTimePattern['id'],
                        label: deviceTimePattern['tp-name'],
                        optionalColumns: [{ key: 'site', value: deviceTimePattern['site']?.['site-name'] }],
                        isDefault: deviceTimePattern['is-default']
                    };
                });

                return resolve({ rows: deviceTimePatternNames, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getDeviceTimePatternNamesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        let url = `timePattern?fields[timePattern]=tpName,isDefault${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, deviceTimePatterns: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const deviceTimePatternNames: Name[] = deviceTimePatterns.map((deviceTimePattern): Name => {
                    return { value: deviceTimePattern['id'], label: deviceTimePattern['tp-name'], isDefault: deviceTimePattern['is-default']};
                });

                return resolve({ rows: deviceTimePatternNames, pagination: newPagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getDeviceTimePatterns = async (translate: Translator, listParameters?: ListQuery): Promise<DeviceTimePatternWrapper> => {
    try {
        const searchOnNames: string[] = ['tpMonday', 'tpTuesday', 'tpWednesday', 'tpThursday', 'tpFriday', 'tpSaturday', 'tpSunday', 'tpPublicHoliday'];
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, customFilterBuilder(searchOnNames, translate, listParameters?.filters, listParameters?.searchTerm));
        const fieldsTimePatern = 'fields[timePattern]=tpName,tpDuration,tpStartDate,tpEndDate,tpStartTime,tpDefault,tpMonday,tpTuesday,tpWednesday,tpThursday,tpFriday,tpSaturday,tpSunday,tpPublicHoliday,isDefault,site';
        const fieldsSite = 'fields[site]=siteName';
        let url = `timePattern?include=site${partialURL}&${fieldsTimePatern}&${fieldsSite}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const rows: DeviceTimePattern[] = data.map((deviceTimePattern): DeviceTimePattern => {
                    return {
                        id: deviceTimePattern['id'],
                        name: deviceTimePattern['tp-name'],
                        duration: convertToDuration(deviceTimePattern['tp-duration']),
                        days: getDays(
                            deviceTimePattern['tp-monday'],
                            deviceTimePattern['tp-tuesday'], 
                            deviceTimePattern['tp-wednesday'],
                            deviceTimePattern['tp-thursday'], 
                            deviceTimePattern['tp-friday'], 
                            deviceTimePattern['tp-saturday'], 
                            deviceTimePattern['tp-sunday'],
                            deviceTimePattern['tp-public-holiday'], 
                            translate),
                        endDate: getFormattedDate(deviceTimePattern['tp-end-date']),
                        startDate: getFormattedDate(deviceTimePattern['tp-start-date']),
                        startTime: convertToTime(deviceTimePattern['tp-start-time']),
                        site: deviceTimePattern['site']['site-name'],
                        default: deviceTimePattern['tp-default'],
                        originalName: deviceTimePattern['tp-name'],
                        isDefault: deviceTimePattern['is-default']
                    }
                });


                return resolve({ rows, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getDeviceTimePatternById = async (deviceTimePatternId: string, translate: Translator): Promise<DeviceTimePattern> => {
    try {        
        const timePatternFields = 'fields[timePattern]=tpEndDate,tpStartDate,tpStartTime,tpName,tpDuration,tpFriday,totalTimeTriggeredActions,timeTriggeredActions,tpPublicHoliday,tpMonday,tpTuesday,tpWednesday,tpThursday,tpSaturday,tpSunday,tpDefault,isDefault,site'
        const siteFields = 'fields[site]=siteName'
        const url = `timePattern/${deviceTimePatternId}?include=site&${timePatternFields}&${siteFields}`
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const endDate = data['tp-end-date'];
                const startDate = data['tp-start-date'];
                const startTime = data['tp-start-time'];

                const deviceTimePattern: DeviceTimePattern = {
                    id: data['id'],
                    name: data['tp-name'],
                    duration: convertToDuration(data['tp-duration']),
                    endTime: (startTime ? convertToEndTime((data['tp-start-time']).toString(), (data['tp-duration']).toString()) : ''),
                    days: getDays(
                        data['tp-monday'], 
                        data['tp-tuesday'], 
                        data['tp-wednesday'], 
                        data['tp-thursday'], 
                        data['tp-friday'], 
                        data['tp-saturday'], 
                        data['tp-sunday'], 
                        data['tp-public-holiday'], 
                        translate),
                    site: data['site']['site-name'],
                    siteId: data['site']['id'],
                    timeTriggeredActions: data['total-time-triggered-actions'],
                    dayList: buildDayList(
                        data['tp-monday'],
                        data['tp-tuesday'],
                        data['tp-wednesday'],
                        data['tp-thursday'],
                        data['tp-friday'],
                        data['tp-saturday'],
                        data['tp-sunday'],
                        data['tp-public-holiday'],
                    ),
                    default: data['tp-default'],
                    isDefault: data['is-default']
                }

                if (startTime) {
                    deviceTimePattern.startTime = convertToTime(startTime);
                }

                if (endDate) {
                    deviceTimePattern.endDate = convertNumberToDate(endDate);
                }

                if (startDate) {
                    deviceTimePattern.startDate = convertNumberToDate(startDate)
                }

                return resolve(deviceTimePattern);
            });

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeDeviceTimePattern = async (deviceTimePattern: DeviceTimePattern, createDeviceTimePattern = true): Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            data: {
                type: 'timePattern',
                id: deviceTimePattern.id,
                attributes: {
                    tpDefault: deviceTimePattern.default ?? 0,
                    tpExactEndTime: deviceTimePattern.endTimeExtract, // tpExactEndTime is only used for save. Not for loading.
                    tpEndDate: deviceTimePattern.endDate ? convertDateToNumber(deviceTimePattern.endDate) : "0",
                    tpFriday: deviceTimePattern.dayList?.includes(Days.friday),
                    tpMonday: deviceTimePattern.dayList?.includes(Days.monday),
                    tpName: deviceTimePattern.name,
                    tpNo: deviceTimePattern.id,
                    tpPublicHoliday: deviceTimePattern.dayList?.includes(Days.holidays),
                    tpSaturday: deviceTimePattern.dayList?.includes(Days.saturday),
                    tpStartDate: deviceTimePattern.startDate ? convertDateToNumber(deviceTimePattern.startDate) : "0",
                    tpExactStartTime: deviceTimePattern.startTime, // tpExactStartTime is only used for save. Not for loading.
                    tpSunday: deviceTimePattern.dayList?.includes(Days.sunday),
                    tpThursday: deviceTimePattern.dayList?.includes(Days.thursday),
                    tpTuesday: deviceTimePattern.dayList?.includes(Days.tuesday),
                    tpTypeId: 0,
                    tpWednesday: deviceTimePattern.dayList?.includes(Days.wednesday),
                    tpExactDuration: deviceTimePattern.duration
                },
                relationships: {
                    site: {
                        data: {
                            type: 'site',
                            id: deviceTimePattern.siteId
                        }
                    }
                }
            }
        };

        const url = `timePattern${createDeviceTimePattern ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createDeviceTimePattern, newRecord)
            .then(data => {
                response = data;
            }).catch(error => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                })
            });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createDeviceTimePattern) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }
                    new Deserializer().deserialize(content, (error, newDeviceTimePattern) => {
                        if (error) {
                            return reject({ status: response.status, id: deviceTimePattern.id, success: false, message: genericError });
                        }
                        return resolve({ id: newDeviceTimePattern.id, success: true });
                    })
                    return
                }
                if (response.status === NO_CONTENT) {
                    return resolve({ id: deviceTimePattern.id, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: deviceTimePattern.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }
            return reject({ status: INTERNAL_SERVER_ERROR, id: deviceTimePattern.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deleteDeviceTimePattern = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `timePattern/${id}`;
        const deviceTImePattern = {
            data: {
                type: "timePattern",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, deviceTImePattern)
        return new Promise(async (resolve, reject) => {
            if (response.status === 204) {
                return resolve({ id: id, success: true })
            }
            const json = await response.json();

            return reject({ status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getFilteredDeviceTimePatternNamesWithPagination = async (filters?: Filter[], pagination?: PaginationModel): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const partialURL = createPartialURL(pagination?.pageNumber, pagination?.maxPageSize, undefined, getFilterClause(filters));
        let url = `timePattern?fields[timePattern]=tpName${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, areas: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const deviceTimePatternNames: Name[] = areas.map((area): Name => {
                    return { value: area['id'], label: area['tp-name'] };
                });

                return resolve({ rows: deviceTimePatternNames, pagination: newPagination });
            });

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const save = async (url, timeTriggeredActions, create: false) => {
    try {
        let response: Response;
        await apiConnector.sendData(url, create, timeTriggeredActions).then(async (data) => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise(async (resolve, reject) => {
            if (response && isSuccessStatus(response.status)) {
                return resolve({success:true});
            }
            return reject({ status: response.status, message: genericError });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const assignDeviceTimePattern = async (toBeAssignedAccessGroups: string[], timePattern: string) => {
    if (toBeAssignedAccessGroups.length === 0) {
        return Promise.resolve({success:true})
    }
   const newTimeTriggeredActions = buildTimeTriggeredActionsToBeSaved(toBeAssignedAccessGroups, timePattern);

    const addedTimeTriggeredActions = newTimeTriggeredActions.map(async (timeTriggeredAction) => {
        const url = `timeTriggeredAction/${timeTriggeredAction.data.id}`;
         await save(url, timeTriggeredAction, false)

    });

    return Promise.allSettled([...addedTimeTriggeredActions])
};

const buildTimeTriggeredActionsToBeSaved = (toBeAssigned: string[], timePatternId: string) => {
    return toBeAssigned.map((timeTriggeredActionId) => {
        return ({
            data: {
                type: "timeTriggeredAction",
                id: timeTriggeredActionId,
                relationships: {
                    timePattern: {
                        data: {
                            type: "timePattern",
                            id: timePatternId,
                        },
                    },
                },
            }
        })
    });
};
